"use client";

import { useEffect, useRef } from "react";
import { useUser } from "@clerk/nextjs";
import posthog from "posthog-js";

/**
 * Identifies the visitor to PostHog using the Clerk userId once Clerk has
 * loaded a signed-in session. Anonymous landing events (most of the funnel
 * top) merge onto this person automatically at identify time, so we preserve
 * the full path: anonymous Zenyra session → gate signup → continued events.
 *
 * Mount once at the app root (`app/layout.tsx`).
 */
export default function PosthogIdentifier() {
  const { isLoaded, isSignedIn, user } = useUser();
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn || !user?.id) {
      // Signed out — reset so a future signed-in user gets a clean identify
      // (no leaking previous person's properties).
      if (identifiedRef.current) {
        try {
          posthog.reset();
        } catch {
          /* posthog not initialized */
        }
        identifiedRef.current = null;
      }
      return;
    }

    if (identifiedRef.current === user.id) return;
    identifiedRef.current = user.id;
    try {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || undefined,
      });
    } catch {
      /* posthog not initialized */
    }
  }, [isLoaded, isSignedIn, user]);

  return null;
}
